import AuthImage from "../AuthImage";
import { ArrowLeft, ArrowRight, Camera, Sparkles, Check, Upload } from "lucide-react";

const STEPS = ["Front", "Right side", "Back", "Left side"];
const KEYS = ["front", "side", "back", "left_side"];
const LAST = STEPS.length - 1;

function PhotoTile({ label, src, uploading, active, done }) {
  let preview;
  if (src) {
    preview = <AuthImage path={src} alt={label} className="w-full h-32 sm:h-40 object-cover rounded-xl" />;
  } else if (uploading) {
    preview = (
      <div className="w-full h-32 sm:h-40 rounded-xl bg-paper-100 flex items-center justify-center text-ink-500">
        <div className="w-6 h-6 rounded-full border-2 border-sage-500 border-t-transparent animate-spin" />
      </div>
    );
  } else {
    preview = (
      <div className="w-full h-32 sm:h-40 rounded-xl bg-paper-100 flex items-center justify-center text-ink-500">
        <Camera className="w-6 h-6" strokeWidth={1.5} />
      </div>
    );
  }
  let borderCls = "border-paper-200 bg-paper-100/40";
  if (active) borderCls = "border-sage-500 bg-white";
  else if (src) borderCls = "border-sage-500/40 bg-white";
  return (
    <div className={`rounded-2xl p-4 border-2 ${borderCls}`}>
      <div className="label-overline mb-2 flex items-center justify-between">
        <span>{label}</span>
        {done && <Check className="w-3.5 h-3.5 text-sage-600" />}
      </div>
      {preview}
    </div>
  );
}

export default function PhotoUploadSection({
  step,
  setStep,
  photos,
  uploading,
  onSelectFile,
  runAnalysis,
  allPhotosDone,
}) {
  const stepKey = KEYS[step];

  let libraryLabel;
  if (uploading[stepKey]) libraryLabel = "Uploading…";
  else if (photos[stepKey]) libraryLabel = "Replace from library";
  else libraryLabel = "Upload from library";

  return (
    <div data-testid="capture-phase">
      <div className="label-overline mb-2">Step {step + 1} of 4</div>
      <h1 className="font-heading text-4xl font-light tracking-tight text-ink-900 mb-2">{STEPS[step]} view</h1>
      <p className="text-ink-700 mb-10 max-w-xl">
        {step === 0 && "Have the client stand facing you, arms relaxed at their sides. Capture the full body, head to ankles."}
        {step === 1 && "Ask the client to make a quarter-turn so their RIGHT side faces the camera (the client's right, not yours). Capture head to ankles to assess pelvic tilt and head position."}
        {step === 2 && "Another quarter-turn in the same direction — the client is now facing away. Capture the back; look for shoulder-height differences."}
        {step === 3 && "One more quarter-turn in the same direction so the client's LEFT side now faces the camera. Capture head to ankles."}
      </p>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-10">
        {STEPS.map((label, i) => {
          const key = KEYS[i];
          return (
            <PhotoTile
              key={key}
              label={label}
              src={photos[key]}
              uploading={uploading[key]}
              active={i === step}
              done={!!photos[key]}
            />
          );
        })}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="photo-tile rounded-3xl p-8 sm:p-10 cursor-pointer flex flex-col items-center justify-center text-center block" data-testid={`capture-${stepKey}-area`}>
          <Camera className="w-10 h-10 text-sage-500 mb-3" strokeWidth={1.5} />
          <div className="font-heading text-lg text-ink-900 mb-1">
            {photos[stepKey] ? "Retake with camera" : "Take a photo"}
          </div>
          <div className="text-xs text-ink-500">Open camera directly</div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            capture="environment"
            onChange={(e) => onSelectFile(e, stepKey)}
            className="hidden"
            data-testid={`capture-${stepKey}-input`}
          />
        </label>
        <label className="photo-tile rounded-3xl p-8 sm:p-10 cursor-pointer flex flex-col items-center justify-center text-center block" data-testid={`upload-${stepKey}-area`}>
          <Upload className="w-10 h-10 text-terracotta-500 mb-3" strokeWidth={1.5} />
          <div className="font-heading text-lg text-ink-900 mb-1">{libraryLabel}</div>
          <div className="text-xs text-ink-500">Pick a saved photo</div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => onSelectFile(e, stepKey)}
            className="hidden"
            data-testid={`upload-${stepKey}-input`}
          />
        </label>
      </div>

      <div className="flex items-center justify-between mt-10">
        <button
          data-testid="prev-step-btn"
          onClick={() => setStep(Math.max(0, step - 1))}
          disabled={step === 0}
          className="min-h-[48px] px-5 rounded-full text-ink-700 hover:bg-paper-100 disabled:opacity-30 transition-all inline-flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" /> Previous
        </button>
        {step < LAST ? (
          <button
            data-testid="next-step-btn"
            onClick={() => setStep(step + 1)}
            disabled={!photos[stepKey]}
            className="min-h-[48px] px-6 rounded-full bg-sage-500 hover:bg-sage-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-40 transition-all"
          >
            Next <ArrowRight className="w-4 h-4" />
          </button>
        ) : (
          <button
            data-testid="analyze-btn"
            onClick={runAnalysis}
            disabled={!allPhotosDone}
            className="min-h-[48px] px-6 rounded-full bg-terracotta-500 hover:bg-terracotta-600 text-white font-medium inline-flex items-center gap-2 disabled:opacity-40 transition-all shadow-sm hover:shadow-md"
          >
            <Sparkles className="w-4 h-4" /> Analyze posture
          </button>
        )}
      </div>
    </div>
  );
}
